import React, { useState, useRef, useEffect } from 'react';
import '../styles/MonthSelector.css';

const MonthSelector = ({ value, onChange, label, id, placeholder }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(value || null);
  const [displayDate, setDisplayDate] = useState(new Date());
  const selectorRef = useRef(null);

  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth();
  const currentMonthYear = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}`;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (selectorRef.current && !selectorRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (value) {
      setSelectedMonth(value);
      const [year, month] = value.split('-');
      setDisplayDate(new Date(parseInt(year), parseInt(month) - 1, 1));
    } else {
      // Auto-select current month if no value is set
      setSelectedMonth(currentMonthYear);
      setDisplayDate(new Date(currentYear, currentMonth, 1));
      // Keep parent form state in sync with the visual default month
      onChange(currentMonthYear);
    }
  }, [value, currentMonthYear, currentYear, currentMonth, onChange]);

  // Auto-select current month when opening if no value is set
  useEffect(() => {
    if (isOpen && !value) {
      setSelectedMonth(currentMonthYear);
      setDisplayDate(new Date(currentYear, currentMonth, 1));
      onChange(currentMonthYear);
    }
  }, [isOpen, value, currentMonthYear, currentYear, currentMonth, onChange]);

  const monthNames = [
    { value: '01', label: 'January', short: 'Jan' },
    { value: '02', label: 'February', short: 'Feb' },
    { value: '03', label: 'March', short: 'Mar' },
    { value: '04', label: 'April', short: 'Apr' },
    { value: '05', label: 'May', short: 'May' },
    { value: '06', label: 'June', short: 'Jun' },
    { value: '07', label: 'July', short: 'Jul' },
    { value: '08', label: 'August', short: 'Aug' },
    { value: '09', label: 'September', short: 'Sep' },
    { value: '10', label: 'October', short: 'Oct' },
    { value: '11', label: 'November', short: 'Nov' },
    { value: '12', label: 'December', short: 'Dec' },
  ];

  const handleMonthSelect = (monthValue) => {
    const year = displayDate.getFullYear();
    const monthYear = `${year}-${monthValue}`;
    setSelectedMonth(monthYear);
    onChange(monthYear);
    setIsOpen(false);
  };

  const handlePrevYear = () => {
    setDisplayDate(new Date(displayDate.getFullYear() - 1, displayDate.getMonth(), 1));
  };

  const handleNextYear = () => {
    setDisplayDate(new Date(displayDate.getFullYear() + 1, displayDate.getMonth(), 1));
  };


  const handleClear = () => {
    setSelectedMonth(null);
    onChange('');
    setIsOpen(false);
  };

  const getDisplayValue = () => {
    if (!selectedMonth) return placeholder || 'Select Month';
    const [year, month] = selectedMonth.split('-');
    const monthObj = monthNames.find(m => m.value === month);
    return monthObj ? `${monthObj.label} ${year}` : selectedMonth;
  };

  return (
    <div className="month-selector-wrapper" ref={selectorRef}>
      {label && <label htmlFor={id} className="month-selector-label">{label}</label>}
      <div className="month-selector">
        <input
          id={id}
          type="text"
          value={getDisplayValue()}
          readOnly
          placeholder={placeholder || 'Select Month'}
          onClick={() => setIsOpen(!isOpen)}
          className="month-selector-input"
        />
        <span className="month-selector-icon" onClick={() => setIsOpen(!isOpen)}>
          ▼
        </span>
        {isOpen && (
          <div className="month-selector-dropdown">
            <div className="month-selector-calendar">
              {/* Header with Year and Navigation */}
              <div className="month-selector-header">
                <div className="month-selector-year-display">
                  {displayDate.getFullYear()}
                </div>
                <div className="month-selector-nav-arrows">
                  <button
                    type="button"
                    className="month-selector-nav-arrow"
                    onClick={handlePrevYear}
                    aria-label="Previous Year"
                  >
                    ▲
                  </button>
                  <button
                    type="button"
                    className="month-selector-nav-arrow"
                    onClick={handleNextYear}
                    aria-label="Next Year"
                  >
                    ▼
                  </button>
                </div>
              </div>

              {/* Month Names Grid */}
              <div className="month-selector-months-grid">
                {monthNames.map((month) => {
                  const monthYear = `${displayDate.getFullYear()}-${month.value}`;
                  const isSelected = selectedMonth === monthYear;
                  const isCurrentMonth = monthYear === currentMonthYear;
                  
                  return (
                    <div
                      key={month.value}
                      className={`month-selector-month-cell ${
                        isSelected ? 'selected' : ''
                      } ${isCurrentMonth ? 'current-month' : ''}`}
                      onClick={() => handleMonthSelect(month.value)}
                    >
                      {month.short}
                    </div>
                  );
                })}
              </div>

              {/* Footer */}
              <div className="month-selector-footer">
                <button
                  type="button"
                  className="month-selector-footer-button"
                  onClick={handleClear}
                >
                  Clear
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MonthSelector;

